import { useCallback, useContext, useMemo, useState } from 'react'
import { CoreContext } from 'context/CoreContext'
import { apiRequest, exposeStrapiError, parseStrapiImage } from 'utils'
import { languages } from 'utils/options'
import {
  CreateFolder,
  Delete as DeleteFavorite,
  DeleteFolder,
  MoveFavorites,
  ReadWorkspace,
  ReorderFavorites,
  ReorderFolders,
  UpdateFolder,
} from 'services/favorites'

const normalizeItem = (item) => ({
  ...item,
  favoriteId: item?.favorite_id || item?.id,
  contentId: item?.content_id,
  folderId: item?.folder_id || null,
  type: item?.content_type,
  id: item?.content_id,
  refid: item?.favorite_id || item?.id,
  title: item?.title,
  cover: parseStrapiImage(item?.image?.url),
  image: item?.image ? { ...item.image, url: parseStrapiImage(item?.image?.url) } : null,
  document: item?.document?.url ? parseStrapiImage(item.document.url) : null,
  lesson: item?.lesson,
  lesson_title: item?.lesson_title,
  uid: item?.uid,
  documentId: item?.documentId,
  document_type: item?.document_type,
  position: item?.position,
  isFavorite: true,
})

const normalizeWorkspace = (result) => {
  const data = result?.data || {}

  return {
    folders: (data?.folders || []).map((folder, index) => ({
      id: folder?.id,
      name: folder?.name,
      position: folder?.position ?? index + 1,
      count: folder?.count || 0,
    })),
    items: (data?.items || []).map(normalizeItem),
    unfiledCount: data?.unfiledCount || 0,
  }
}

const recalculateFolderCounts = (workspace) => {
  const counts = workspace.items.reduce((acc, item) => {
    const key = item?.folderId == null ? 'unfiled' : `${item.folderId}`
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

  return {
    ...workspace,
    folders: workspace.folders.map((folder) => ({
      ...folder,
      count: counts[`${folder.id}`] || 0,
    })),
    unfiledCount: counts.unfiled || 0,
  }
}

const sortItems = (items = []) => [...items].sort((a, b) => {
  const posA = Number.isFinite(a?.position) ? a.position : Number.MAX_SAFE_INTEGER
  const posB = Number.isFinite(b?.position) ? b.position : Number.MAX_SAFE_INTEGER

  if (posA !== posB) return posA - posB
  return (b?.favoriteId || 0) - (a?.favoriteId || 0)
})

const withReorderedFolderItems = (workspace, folderId, orderedFavoriteIds) => {
  const targetKey = folderId == null ? null : Number(folderId)
  const requestedOrder = orderedFavoriteIds.map((id) => Number(id))
  const targetItems = sortItems(
    workspace.items.filter((item) => (item?.folderId ?? null) === targetKey)
  )
  const targetMap = new Map(targetItems.map((item) => [item.favoriteId, item]))
  const ordered = [
    ...requestedOrder.map((id) => targetMap.get(id)).filter(Boolean),
    ...targetItems.filter((item) => !requestedOrder.includes(item.favoriteId)),
  ].map((item, index) => ({
    ...item,
    position: index + 1,
  }))

  const orderedMap = new Map(ordered.map((item) => [item.favoriteId, item]))

  return recalculateFolderCounts({
    ...workspace,
    items: workspace.items.map((item) => orderedMap.get(item.favoriteId) || item),
  })
}

const withMovedFavorites = (workspace, favoriteIds, targetFolderId) => {
  const targetKey = targetFolderId == null ? null : Number(targetFolderId)
  const requestedIds = favoriteIds.map((id) => Number(id))
  const requestedSet = new Set(requestedIds)
  const targetItems = sortItems(
    workspace.items.filter((item) => {
      if (requestedSet.has(item.favoriteId)) return false
      return (item?.folderId ?? null) === targetKey
    })
  )

  let nextPosition = targetItems.length
  const movedMap = new Map()

  requestedIds.forEach((favoriteId) => {
    const current = workspace.items.find((item) => item.favoriteId === favoriteId)
    if (!current) return
    nextPosition += 1
    movedMap.set(favoriteId, {
      ...current,
      folderId: targetKey,
      position: nextPosition,
    })
  })

  return recalculateFolderCounts({
    ...workspace,
    items: workspace.items.map((item) => movedMap.get(item.favoriteId) || item),
  })
}

const withRemovedFavorite = (workspace, favoriteId) => recalculateFolderCounts({
  ...workspace,
  items: workspace.items.filter((item) => item.favoriteId !== favoriteId),
})

const withReorderedFolders = (workspace, orderedFolderIds) => {
  const folderMap = new Map(workspace.folders.map((folder) => [folder.id, folder]))
  const requestedIds = orderedFolderIds.map((id) => Number(id))
  const ordered = [
    ...requestedIds.map((id) => folderMap.get(id)).filter(Boolean),
    ...workspace.folders.filter((folder) => !requestedIds.includes(folder.id)),
  ].map((folder, index) => ({
    ...folder,
    position: index + 1,
  }))

  return {
    ...workspace,
    folders: ordered,
  }
}

export default function useFavoritesWorkspace() {
  const { language } = useContext(CoreContext)
  const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || 'en', [language])
  const [workspace, setWorkspace] = useState({
    folders: [],
    items: [],
    unfiledCount: 0,
  })
  const [loading, setLoading] = useState(false)
  const [busyState, setBusyState] = useState({
    favoriteIds: [],
    folderIds: [],
    creatingFolder: false,
  })

  const loadWorkspace = useCallback(async () => {
    setLoading(true)
    const result = await apiRequest(() => ReadWorkspace(locale))

    if (result && !exposeStrapiError(result)) {
      setWorkspace(normalizeWorkspace(result))
    }

    setLoading(false)
  }, [locale])

  const removeFavorite = useCallback(async (favoriteId) => {
    if (!favoriteId) return false

    setBusyState((prev) => ({
      ...prev,
      favoriteIds: [...new Set([...prev.favoriteIds, favoriteId])],
    }))

    const result = await apiRequest(() => DeleteFavorite(favoriteId))

    if (exposeStrapiError(result)) {
      setBusyState((prev) => ({
        ...prev,
        favoriteIds: prev.favoriteIds.filter((id) => id !== favoriteId),
      }))
      return false
    }

    setWorkspace((prev) => withRemovedFavorite(prev, favoriteId))
    setBusyState((prev) => ({
      ...prev,
      favoriteIds: prev.favoriteIds.filter((id) => id !== favoriteId),
    }))
    return true
  }, [])

  const createFolder = useCallback(async (name) => {
    setBusyState((prev) => ({ ...prev, creatingFolder: true }))
    const result = await apiRequest(() => CreateFolder({ name }))

    setBusyState((prev) => ({ ...prev, creatingFolder: false }))

    if (exposeStrapiError(result)) return false
    await loadWorkspace()
    return result?.data || null
  }, [loadWorkspace])

  const renameFolder = useCallback(async (folderId, name) => {
    setBusyState((prev) => ({
      ...prev,
      folderIds: [...new Set([...prev.folderIds, folderId])],
    }))

    const result = await apiRequest(() => UpdateFolder(folderId, { name }))

    setBusyState((prev) => ({
      ...prev,
      folderIds: prev.folderIds.filter((id) => id !== folderId),
    }))

    if (exposeStrapiError(result)) return false
    await loadWorkspace()
    return true
  }, [loadWorkspace])

  const deleteFolder = useCallback(async (folderId) => {
    setBusyState((prev) => ({
      ...prev,
      folderIds: [...new Set([...prev.folderIds, folderId])],
    }))

    const result = await apiRequest(() => DeleteFolder(folderId))

    setBusyState((prev) => ({
      ...prev,
      folderIds: prev.folderIds.filter((id) => id !== folderId),
    }))

    if (exposeStrapiError(result)) return false
    await loadWorkspace()
    return true
  }, [loadWorkspace])

  const moveFavoritesToFolder = useCallback(async (favoriteIds, targetFolderId) => {
    const ids = favoriteIds.map((id) => Number(id)).filter(Boolean)
    if (!ids.length) return false

    const previousWorkspace = workspace
    setBusyState((prev) => ({
      ...prev,
      favoriteIds: [...new Set([...prev.favoriteIds, ...ids])],
    }))
    setWorkspace((prev) => withMovedFavorites(prev, ids, targetFolderId))

    const result = await apiRequest(() => MoveFavorites({
      favoriteIds: ids,
      targetFolderId,
    }))

    if (exposeStrapiError(result)) {
      setWorkspace(previousWorkspace)
      setBusyState((prev) => ({
        ...prev,
        favoriteIds: prev.favoriteIds.filter((id) => !ids.includes(id)),
      }))
      return false
    }

    setBusyState((prev) => ({
      ...prev,
      favoriteIds: prev.favoriteIds.filter((id) => !ids.includes(id)),
    }))
    return true
  }, [workspace])

  const reorderFolders = useCallback(async (orderedFolderIds) => {
    const ids = orderedFolderIds.map((id) => Number(id)).filter(Boolean)
    if (!ids.length) return false

    const previousWorkspace = workspace
    setWorkspace((prev) => withReorderedFolders(prev, ids))

    const result = await apiRequest(() => ReorderFolders({
      orderedFolderIds: ids,
    }))

    if (exposeStrapiError(result)) {
      setWorkspace(previousWorkspace)
      return false
    }

    return true
  }, [workspace])

  const reorderFavoritesInFolder = useCallback(async (folderId, orderedFavoriteIds) => {
    const ids = orderedFavoriteIds.map((id) => Number(id)).filter(Boolean)
    if (!ids.length) return false

    const previousWorkspace = workspace
    setWorkspace((prev) => withReorderedFolderItems(prev, folderId, ids))

    const result = await apiRequest(() => ReorderFavorites({
      folderId,
      orderedFavoriteIds: ids,
    }))

    if (exposeStrapiError(result)) {
      setWorkspace(previousWorkspace)
      return false
    }

    return true
  }, [workspace])

  const isFavoriteBusy = useCallback((favoriteId) => busyState.favoriteIds.includes(favoriteId), [busyState.favoriteIds])
  const isFolderBusy = useCallback((folderId) => busyState.folderIds.includes(folderId), [busyState.folderIds])

  return useMemo(() => ({
    workspace,
    loading,
    busyState,
    loadWorkspace,
    removeFavorite,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFavoritesToFolder,
    reorderFolders,
    reorderFavoritesInFolder,
    isFavoriteBusy,
    isFolderBusy,
  }), [
    workspace,
    loading,
    busyState,
    loadWorkspace,
    removeFavorite,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFavoritesToFolder,
    reorderFolders,
    reorderFavoritesInFolder,
    isFavoriteBusy,
    isFolderBusy,
  ])
}
